import { beschriftung, positionsbezeichnung, t } from './sprache'
import type { Sprachschluessel } from './sprache'
import { ohneWert } from './Ohnewert'

/**
 * Der Aufriss ist ein **Pfad**, keine Einstellung.
 *
 * Eine Position lässt sich nach Sachkonten aufreißen, darin nach
 * Gesellschaften, darin nach Kostenstellen — oder in jeder anderen
 * Reihenfolge. Jede Ebene fragt neu, welche Achse sie noch nicht verbraucht
 * hat. Genau darum ging es beim Fall, der das ausgelöst hat:
 *
 * > Die Vorräte springen in einem Monat. Rechtsklick auf das Konto, „nach
 * > Gesellschaften" — und darunter steht, bei wem. **Die Monatsspalten bleiben
 * > dabei stehen.** Ein Wechsel ins Bild „Konzernstruktur" zwänge dazu, den
 * > untersuchten Monat auszuwählen, und nähme den Vergleich weg, der die
 * > Auffälligkeit überhaupt sichtbar gemacht hat.
 *
 * Deshalb liegt der Pfad hier und nicht in einem Bauteil: Er gilt in allen
 * Spaltenbildern gleich, weil er die **Zeilen**achse betrifft und die Spalten
 * nichts davon wissen.
 *
 * **Zwei Sorten Achsen, ein Pfad.** Sachkonto und Gesellschaft reisen mit
 * jedem Bericht mit — sie stehen im festgeschriebenen Ergebnis. Partner,
 * Segment und die Analysedimensionen des Kunden (B9) werden **auf Abruf**
 * gelesen, aus der Faktenzeile des Laufs (R214). Für den Pfad ist das
 * gleichgültig: Eine Achse ist eine Achse. Kommt eine Dimension zum
 * Datenmodell dazu, kommt sie hier dazu, ohne dass ein Bildschirm angefasst
 * wird (7.3 Abschnitt 2f).
 */

/** Eine Achse, entlang derer aufgerissen wird — die zwei Grundachsen oder eine Zusatzachse. */
export type Achse = string

/** Die beiden Achsen, die jeder Bericht mitbringt. */
export const GRUNDACHSEN: readonly Achse[] = ['konten', 'einheiten']

export function istGrundachse(achse: Achse): achse is 'konten' | 'einheiten' {
  return achse === 'konten' || achse === 'einheiten'
}

/**
 * Die Vorgabe ist **Sachkonten**.
 *
 * „Woraus besteht diese Position" ist die Frage, die im Bericht häufiger
 * gestellt wird als „wer trägt dazu bei" — und wer sie anders will, stellt sie
 * einmal für den ganzen Bericht um statt dreißigmal je Zeile.
 */
export const VORGABE: Achse = 'konten'

/**
 * Eine Zelle, wie sie aus allen Berichtsarten kommt.
 *
 * Auf den Grundachsen heißt `null` „Summe". Die Zusatzachsen stehen in
 * `weitere`: **Ein fehlender Schlüssel heißt Summe, `null` heißt „nicht
 * zugeordnet"** — ein benannter Wert, der die Differenz zur Summe erklärt
 * (2.4 Abschnitt 3.7). Eine Konsolidierungsbuchung trägt keine Kostenstelle;
 * in einer Auswertung je Kostenstelle steht sie genau dort.
 */
export type Zelle = {
  einheit: string | null
  konto: string | null
  weitere?: Record<string, string | null>
  /** Per Saldowechsel unter einer fremden Position (R1622) — der Name trägt dann den Zusatz. */
  gewechselt?: 'haben' | 'soll' | null
  /** Das Stellvertreterkonto, unter dem dieses Konto steht (R1624, R1625). */
  unter?: string | null
  /**
   * Der letzte Monat des Kontos an dieser Position — gesetzt, wenn seine
   * Zuordnung endet oder es stillgelegt ist (7.3 Abschnitt 33, R1724).
   */
  giltBis?: number | null
}

/** Wie die Kennung eines Stellvertreterkontos beginnt — dahinter steht die Quellposition (R1624). */
export const STELLVERTRETER = 'SW:'

/** Ob diese Kontokennung ein Stellvertreterkonto ist (R1624) — kein Konto des Kontenrahmens. */
export function istStellvertreter(konto: string | null | undefined): boolean {
  return konto !== null && konto !== undefined && konto.startsWith(STELLVERTRETER)
}

/**
 * Eine Zusatzachse, wie der Bericht sie anbietet — mit der Beschriftung des
 * Kunden, wo es eine gibt (B9), und ob in diesen Läufen überhaupt eine Zeile
 * die Achse trägt.
 */
export type Achsenangabe = {
  schluessel: Achse
  code: string
  namen: Record<string, string> | null
  belegt: boolean
}

/**
 * Der aufgeklappte Zustand einer Zeile: der Pfad, den der Anwender genommen
 * hat. `['konten']` heißt „nach Konten aufgeklappt"; `['konten','einheiten']`
 * heißt zusätzlich „und im Konto X nach Gesellschaften".
 */
export type Pfad = Achse[]

/**
 * Was eine Zelle auf einer Achse trägt — oder `undefined`, wenn sie auf
 * dieser Achse die Summe ist.
 *
 * Die eine Stelle, an der die zwei Schreibweisen zusammenkommen: `null` auf
 * einer Grundachse ist Summe, `null` auf einer Zusatzachse ist ein Wert.
 */
export function wertAuf(z: Zelle, achse: Achse): string | null | undefined {
  if (achse === 'konten') {
    return z.konto ?? undefined
  }

  if (achse === 'einheiten') {
    return z.einheit ?? undefined
  }

  return z.weitere && achse in z.weitere ? z.weitere[achse] : undefined
}

/** Alle Achsen, auf denen eine Zelle einen Wert trägt. */
function achsenVon(z: Zelle): Achse[] {
  const achsen: Achse[] = []
  if (z.konto !== null) achsen.push('konten')
  if (z.einheit !== null) achsen.push('einheiten')
  for (const a of Object.keys(z.weitere ?? {})) achsen.push(a)
  return achsen
}

/**
 * Welche Achsen ein Pfad schon verbraucht hat.
 *
 * Nicht die Achse der aktuellen Ebene allein: Wer unter „1200" schon nach
 * Gesellschaften aufgerissen hat, hat **beide** verbraucht, auch wenn die
 * letzte Ebene nur die eine kennt.
 */
export function achsenAusPfad(pfad: Zelle[]): Achse[] {
  const achsen: Achse[] = []
  for (const zelle of pfad) {
    for (const a of achsenVon(zelle)) {
      if (!achsen.includes(a)) achsen.push(a)
    }
  }

  return achsen
}

/** Welche Achsen an dieser Stelle noch zu haben sind — aus dem Angebot des Berichts. */
export function offeneAchsen(bisher: Pfad, zusatz: readonly Achse[] = []): Achse[] {
  return [...GRUNDACHSEN, ...zusatz].filter((a) => !bisher.includes(a))
}

/**
 * Die Zellen einer Ebene.
 *
 * Eine Zelle gehört zur Ebene, wenn sie auf der aufzureißenden Achse einen
 * Wert trägt und auf **jeder anderen** Achse dasselbe sagt wie die Zelle, unter
 * der aufgeklappt wird — die verbrauchten Achsen mit deren Werten, die
 * unberührten als Summe. Sonst mischte die Ebene Summen und Einzelwerte in
 * einer Liste.
 *
 * @param eltern Die Zelle, unter der aufgeklappt wird — leer für die erste Ebene.
 * @param achse Wonach diese Ebene aufreißt.
 */
export function ebene<T extends Zelle>(zellen: T[], eltern: Zelle, achse: Achse): T[] {
  return zellen.filter((z) => {
    if (wertAuf(z, achse) === undefined) {
      return false
    }

    // **Ein Konto hinter einem Stellvertreterkonto erscheint nur unter ihm**
    // (2.7 Abschnitt 3.7b, R1624, R1625) — und unter sich selbst, wenn es
    // weiter aufgerissen wird. Sonst stünden die Konten einer gewechselten
    // Position einzeln unter dem Ziel, und genau das sollte der Stellvertreter
    // verhindern.
    if (z.unter && eltern.konto !== z.unter && eltern.unter !== z.unter) {
      return false
    }

    // Und unter dem Stellvertreter nach Sachkonten stehen nur seine Konten —
    // nicht die übrigen der Zielposition und nicht er selbst.
    if (achse === 'konten' && istStellvertreter(eltern.konto) && z.unter !== eltern.konto) {
      return false
    }

    for (const a of new Set([...GRUNDACHSEN, ...achsenVon(z), ...achsenVon(eltern)])) {
      if (a !== achse && wertAuf(z, a) !== wertAuf(eltern, a)) {
        return false
      }
    }

    return true
  })
}

/**
 * Die Vorgabeachse **dieser** Zeile.
 *
 * Sachkonto, wo es Konten gibt — und Gesellschaft, wo es keine gibt. Eine
 * Zeile, deren Pfeil aufklappt und nichts zeigt, ist ein Versprechen, das beim
 * Anklicken bricht; das trifft die Ergebniszeile der GuV, die eine Summe über
 * alle Positionen ist und deshalb keine eigenen Konten hat.
 */
export function vorgabeFuer(zellen: Zelle[]): Achse {
  return zellen.some((z) => z.konto !== null) ? 'konten' : 'einheiten'
}

/**
 * Die Zellen der **eigenen Konten** einer Position, die zugleich
 * Unterpositionen trägt (7.3 Abschnitt 30, R1621).
 *
 * Unter *C.II. Verbindlichkeiten gegenüber Kreditinstituten* hängen das Konto
 * 3100 und die Unterpositionen C.II.1 und C.II.2. Die Zeile trägt die Zellen
 * aller drei; eigen ist davon nur, was der Bericht in `eigeneKonten` nennt —
 * mit den Kreuzzellen je Gesellschaft, damit der Aufriss darunter weitergeht.
 * Das Konto 1800, das per Saldowechsel unter C.II.2 gewandert ist, steht dort
 * und nicht noch einmal hier.
 *
 * Leer an jeder anderen Zeile: An einer Detailposition sind alle Konten die
 * eigenen, und der Aufriss nach Sachkonto zeigt sie schon.
 */
export function eigeneZellen<T extends Zelle>(
  zeile: { beitraege: T[]; eigeneKonten?: readonly string[] | null },
): T[] {
  const eigene = new Set(zeile.eigeneKonten ?? [])
  return eigene.size === 0 ? [] : zeile.beitraege.filter((z) => z.konto !== null && eigene.has(z.konto))
}

/**
 * Welche Achsen an dieser Stelle wirklich etwas zeigen.
 *
 * Eine Grundachse nur, wenn sie offen **und** gefüllt ist — ein Menüpunkt, der
 * auf eine leere Ebene führt, ist keiner. Eine Zusatzachse, wenn sie offen ist:
 * Ob unter dieser Zelle etwas steht, weiß erst der Abruf; dass der Bericht
 * sie überhaupt trägt, weiß das Angebot (R215).
 */
export function moeglicheAchsen(zellen: Zelle[], pfad: Zelle[], zusatz: readonly Achse[] = []): Achse[] {
  const letzte = pfad[pfad.length - 1] ?? { einheit: null, konto: null }
  // Unter einem Stellvertreterkonto geht es nach Sachkonten weiter — in die
  // Konten der Quellposition (R1625), obwohl der Pfad schon ein Konto trägt.
  const bisher = achsenAusPfad(pfad).filter((a) => !(a === 'konten' && istStellvertreter(letzte.konto)))
  return offeneAchsen(bisher, zusatz).filter(
    (a) => !istGrundachse(a) || ebene(zellen, letzte, a).length > 0,
  )
}

/** Die Kennung, unter der eine Zelle in einem Pfad steht. */
export function zellenschluessel(z: Zelle): string {
  const grund = `${z.einheit ?? ''}|${z.konto ?? ''}`
  const weitere = Object.keys(z.weitere ?? {})
    .sort()
    .map((a) => `${a}=${z.weitere![a] ?? '∅'}`)
  return weitere.length === 0 ? grund : `${grund}|${weitere.join('|')}`
}

/**
 * Die Zelle, die dieselbe Aussage macht wie `zelle` — nur auf **einer Achse
 * festgenagelt**.
 *
 * Das ist die Stelle, an der ein Spaltenbild seine Aufrisszeilen füllt, dessen
 * **Spalten selbst eine Achse sind**. Im Bild *Konzernstruktur* trägt jede
 * Spalte eine Gesellschaft; eine aufgeklappte Kontozeile (`einheit = null`)
 * muss dort ihren Beitrag **je Gesellschaft** zeigen, nicht nur in der Summe.
 *
 * Bis zum 2026-09-09 tat sie das nicht: Unter *B.I. Vorräte* standen vier
 * Konten, alle vier Gesellschaftsspalten leer, und der Wert erschien erst in
 * der Summenspalte. Die Zeile darüber verteilte sich, die Zeile darunter nicht
 * — **R180 verlangt aber ausdrücklich, dass eine Aufrisszeile dieselben
 * Spalten trägt wie die Zeile darüber.** Die Zahlen dafür lagen bereits vor:
 * Der Würfel führt die Kreuzzellen (Gesellschaft *und* Konto) mit.
 *
 * Deshalb steht die Auflösung hier und nicht im Spaltenbild — **ein künftiger
 * Bericht, dessen Spalten eine Achse tragen, erbt sie, statt sie
 * nachzubauen** (R212). Und sie gilt für jede Achse: Im Bild *Partner* trägt
 * jede Spalte einen Partner, und die Suche ist dieselbe.
 *
 * @param zelle Die aufgeklappte Zeile — irgendein Punkt des Würfels.
 * @param achse Welche Achse die Spalte festlegt.
 * @param kennung Der Wert dieser Achse in dieser Spalte — `null` für „Default" (R429).
 * @returns Die passende Zelle, oder `undefined`, wenn diese Spalte nichts trägt.
 */
export function kreuzzelle<T extends Zelle>(
  zellen: T[],
  zelle: Zelle,
  achse: Achse,
  kennung: string | null,
): T | undefined {
  const eigen = wertAuf(zelle, achse)

  // Die Zeile nennt ihre Achse schon selbst: Eine Gesellschaftszeile steht in
  // ihrer eigenen Spalte und in keiner anderen. Dann ist nichts zu suchen —
  // eine Kreuzzelle gäbe es gar nicht.
  if (eigen !== undefined) {
    return eigen === kennung ? (zelle as T) : undefined
  }

  return zellen.find((z) => {
    if (wertAuf(z, achse) !== kennung) {
      return false
    }

    for (const a of new Set([...GRUNDACHSEN, ...achsenVon(z), ...achsenVon(zelle)])) {
      if (a !== achse && wertAuf(z, a) !== wertAuf(zelle, a)) {
        return false
      }
    }

    return true
  })
}

export type Beschriftbar = { code: string; namen: Record<string, string> }

/** Der Stammdatenkatalog, aus dem die Zeilen ihre Namen holen. */
export type Namenskatalog = {
  konten: Map<string, Beschriftbar>
  einheiten: Map<string, Beschriftbar>
  /** Die Partnercodes (R490) — wo einer steht, gilt er vor der Gesellschaft. */
  partner?: Map<string, Beschriftbar>
}

/**
 * Wie eine aufgeklappte Zeile beschriftet wird.
 *
 * Code und Name, wie überall: „1200 · Vorräte", „NL-PROD · Nordlicht
 * Produktion GmbH". Ein Partner ist eine Gesellschaft des Konzerns und heißt
 * wie sie. Eine Kostenstelle trägt ihre Kennung — mehr weiß das System heute
 * nicht über sie. Das ist zugleich der Grund, warum eine aufgeklappte Zeile
 * **keinen** Dimensionsmarker braucht — man sieht ihr an, was sie ist.
 *
 * `null` auf einer Zusatzachse ist der **Vorgabewert der Dimension**, und der
 * steht als Wort da: **„Default"** (2.4 Abschnitt 3.7, 7.3 Abschnitt 5.1,
 * R429). Ein Gedankenstrich wäre keine Ausprägung, sondern die Abwesenheit
 * einer Angabe — und die lässt sich in keiner Abfrage benennen.
 */
export function zellenname(z: Zelle, katalog: Namenskatalog, achse: Achse): string {
  const kennung = wertAuf(z, achse)
  if (kennung === undefined) {
    return ''
  }

  if (kennung === null) {
    return ohneWert('weitere')
  }

  // **Der Partner heisst, wie sein Partnercode heisst** (R490) — und erst
  // hilfsweise wie die Gesellschaft dahinter: Ein Bestand ohne gepflegte
  // Partnercodes bleibt damit lesbar, und ein Partner ohne Gesellschaft
  // (ein Dritter) hat ueberhaupt nur hier einen Namen.
  // Das Stellvertreterkonto heißt nach seiner Quellposition (R1624):
  // „Saldowechsel aus Kursgewinne (Saldowechsel – im Soll)".
  if (achse === 'konten' && istStellvertreter(kennung)) {
    const name = t('saldowechsel.stellvertreter', {
      position: positionsbezeichnung(kennung.slice(STELLVERTRETER.length)),
    })
    return z.gewechselt ? `${name} ${saldowechselzusatz(z.gewechselt)}` : name
  }

  const eintrag =
    achse === 'konten'
      ? katalog.konten.get(kennung)
      : achse === 'partner'
        ? (katalog.partner?.get(kennung) ?? katalog.einheiten.get(kennung))
        : achse === 'einheiten'
          ? katalog.einheiten.get(kennung)
          : undefined

  if (!eintrag) {
    return kennung
  }

  const name = beschriftung(eintrag.namen)
  const grundname = name ? `${eintrag.code} · ${name}` : eintrag.code
  // **Ein gewechseltes Konto sagt es im Namen** (2.7 Abschnitt 3.7b, R1622,
  // R1623): Unter C.II.2 steht „1800 Guthaben bei Kreditinstituten
  // (Saldowechsel – im Haben)", unter B.IV. derselbe Name ohne Zusatz.
  return achse === 'konten' && z.gewechselt ? `${grundname} ${saldowechselzusatz(z.gewechselt)}` : grundname
}

/** Der Zusatz am Namen eines gewechselten Kontos (R1622) — „(Saldowechsel – im Haben)". */
export function saldowechselzusatz(richtung: 'haben' | 'soll'): string {
  return t(richtung === 'haben' ? 'saldowechsel.zusatz.haben' : 'saldowechsel.zusatz.soll')
}

/** Trägt diese Zelle auf der Achse den Vorgabewert „Default"? */
export function istNichtZugeordnet(z: Zelle, achse: Achse): boolean {
  return wertAuf(z, achse) === null
}

/**
 * Die zuletzt geladenen Zusatzachsen — damit auch ein Fenster, das keinen
 * Bericht kennt (der Wertenachweis), eine Kostenstelle beim Namen nennen
 * kann. Ein Bericht setzt sie, sobald er sein Angebot geholt hat.
 */
let bekannt: readonly Achsenangabe[] = []

export function merkeAchsen(angebot: readonly Achsenangabe[]): void {
  bekannt = angebot
}

/**
 * Wie eine Achse heißt — die Grundachsen und Partner aus der Sprachdatei,
 * eine Analysedimension so, wie der Kunde sie benannt hat (B9, R157).
 */
export function achsenname(achse: Achse, angebot: readonly Achsenangabe[] = bekannt): string {
  const fest: Record<string, Sprachschluessel> = {
    konten: 'aufriss.konten',
    einheiten: 'aufriss.einheiten',
    partner: 'aufriss.partner',
    segment: 'aufriss.segment',
    guv: 'aufriss.guv',
  }

  if (fest[achse]) {
    return t(fest[achse]!)
  }

  const angabe = (angebot.length > 0 ? angebot : bekannt).find((a) => a.schluessel === achse)
  return angabe ? beschriftung(angabe.namen ?? {}) || angabe.code : achse
}

/**
 * Was ein Filter über Konto und Gesellschaft hinaus festlegt, als Worte —
 * „Kostenstelle KST-100", „Partner EH-PROD · Nordlicht Produktion", „Partner
 * Default". Für den Kopf eines Nachweises (R205).
 */
export function filtertexte(weitere: Record<string, string | null>, katalog: Namenskatalog): string[] {
  return Object.entries(weitere).map(([achse, wert]) => {
    const name = achsenname(achse)
    if (wert === null) {
      return `${name}: ${ohneWert('weitere')}`
    }

    return `${name} ${zellenname({ einheit: null, konto: null, weitere: { [achse]: wert } }, katalog, achse)}`
  })
}

/**
 * Der Menüpunkt, der eine Ebene nach dieser Achse aufreißt.
 *
 * Die festen Achsen haben ihren eigenen Satz, weil die Mehrzahl im Deutschen
 * nicht mechanisch entsteht („nach Partnern", nicht „nach Partner"). Eine
 * Dimension des Kunden steht in seiner Benennung, in der Einzahl.
 */
export function menuetext(achse: Achse, angebot: readonly Achsenangabe[] = []): string {
  const fest: Record<string, Sprachschluessel> = {
    konten: 'aufriss.menue.konten',
    einheiten: 'aufriss.menue.einheiten',
    partner: 'aufriss.menue.partner',
    segment: 'aufriss.menue.segment',
    guv: 'aufriss.menue.guv',
  }

  return fest[achse] ? t(fest[achse]!) : t('aufriss.menue.dimension', { achse: achsenname(achse, angebot) })
}

/**
 * Der Textmarker an einer Zeile, deren Achse von der Vorgabe abweicht.
 *
 * **Ein Wort, keine Farbe.** Farbe verstärkt, sie unterscheidet nie allein
 * (R143) — ein Pfeil, der sich nur farblich ändert, ist für rund neun Prozent
 * der männlichen Anwender kein Unterschied und in einem Ausdruck gar keiner.
 * Ein gedämpftes Wort ist druckbar, vorlesbar und braucht keine Legende.
 *
 * Und er steht **nur bei Abweichung**: In 400 Zeilen sind 400 Marker, von
 * denen 395 dasselbe sagen, keine Auskunft mehr.
 */
export function markertext(achse: Achse, angebot: readonly Achsenangabe[] = []): string {
  const fest: Record<string, Sprachschluessel> = {
    konten: 'aufriss.marker.konten',
    einheiten: 'aufriss.marker.einheiten',
    partner: 'aufriss.marker.partner',
    segment: 'aufriss.marker.segment',
  }

  return fest[achse] ? t(fest[achse]!) : t('aufriss.marker.dimension', { achse: achsenname(achse, angebot) })
}

/**
 * Der Filter, den ein Pfad über Konto und Gesellschaft hinaus festlegt —
 * das, was der Abruf einer Zusatzachse mitschickt, und was der Wertenachweis
 * einer Zelle erbt (R205).
 */
export function weitereVon(pfad: Zelle[]): Record<string, string | null> {
  const weitere: Record<string, string | null> = {}
  for (const zelle of pfad) {
    Object.assign(weitere, zelle.weitere ?? {})
  }

  return weitere
}
